import React, { useEffect, useState, useMemo } from "react";
import "./SettlementReport.css";
import { useSelector } from "react-redux";
import {
  getSettlementByUTR,
  getSettlementReport,
} from "../../../config/LiveApi";
import { toast } from "react-toastify";
import ExcelDownloader from "./ExcelDownload";
import Pagination from "./Pagination";
import { FaSearch } from "react-icons/fa";
import { IoReload } from "react-icons/io5";
import DateFilter from "../../../components/common/DateFilter";

import DataTable from "../../../components/common/DataTable/DataTable";
import DateCell from "../../../components/common/TableCells/DateCell";
import CopyCell from "../../../components/common/TableCells/CopyCell";
import StatusCell from "../../../components/common/TableCells/StatusCell";

const SettlementTable = () => {
  const token = useSelector((store) => store.userReducer.token);
  const role = useSelector((store) => store.userReducer.role);

  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState({ data: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchUTR, setSearchUTR] = useState("");
  const [searchResult, setSearchResult] = useState(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const getSettlementData = async (start = startDate, end = endDate) => {
    setLoading(true);
    try {
      const response = await getSettlementReport(
        token,
        currentPage,
        start,
        end
      );

      setTableData(response.data);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) getSettlementData();
  }, [currentPage, token]);

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    getSettlementData(start, end);
  };

  const reloadfunction = () => {
    window.location.reload();
  };

  const handleUTRSearch = async () => {
    if (!searchUTR) {
      toast.warning("Please enter a UTR ID");
      return;
    }

    setLoading(true);
    try {
      const response = await getSettlementByUTR(token, searchUTR);

      if (response?.data) {
        setSearchResult(response.data);
      } else {
        setSearchResult(null);
        toast.error("No settlement found for this UTR");
      }
    } catch (err) {
      toast.error("Error fetching settlement by UTR");
    } finally {
      setLoading(false);
    }
  };

  const data = useMemo(() => {
    return searchResult ? [searchResult] : tableData?.data || [];
  }, [searchResult, tableData]);

  const columns = useMemo(() => {
    const cols = [];

    if (role !== "admin") {
      cols.push({
        header: "Provider Name",
        accessor: "providerName",
         render: (row) => row?.providerName,
      });
    }

    cols.push(
      {
        header: "Settlement Amount",
        accessor: "settlementAmount",
         render: (row) => row?.settlementAmount,
      },
      {
        header: "Status",
        render: (row) => (
          <StatusCell
            status={
              row.status === 1 || row.status === true
                ? "SUCCESS"
                : "FAILED"
            }
          />
        ),
      },
      {
        header: "Settlement Delay",
        render: (row) =>
          row.settlementDelay === 1 || row.settlementDelay === true
            ? "Yes"
            : "No",
      },
      {
        header: "OrderId",
        render: (row) => row?.orderId,
      },
      {
        header: "UTR",
        render: (row) => row?.utr,
      },
      {
        header: "Txn Date",
        render: (row) => <DateCell date={row.txnDate} />,
      },
      {
        header: "Created At",
        render: (row) => <DateCell date={row.createdAt} />,
      }
    );

    return cols;
  }, [role]);

  return (
    <div className="settlement-report-container">
      <div className="settlement-report-excel">
        <div className="settlement-report-title">Settlement Report</div>

        <ExcelDownloader
          data={tableData.data}
          fileName="Settlement_Report"
          role={role}
        />
      </div>

      <DateFilter onDateChange={handleDateChange} />

      <div className="settlement-report-excel">
        <div className="serch-section-utr">
          <div className="search-report">
            <input
              type="number"
              className="search-input-report"
              placeholder="Enter UTR Id"
              value={searchUTR}
              onChange={(e) => setSearchUTR(e.target.value)}
            />

            <FaSearch className="search-icon" />

            <button className="go-btn-report" onClick={handleUTRSearch}>
              Go
            </button>

            <button className="reload-btn-report" onClick={reloadfunction}>
              <IoReload />
            </button>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        emptyMessage="No Data Found"
      />

      {!searchResult && (
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
        />
      )}
    </div>
  );
};

export default SettlementTable;